import { Delete, Edit } from "@mui/icons-material"
import ImageIcon from "@mui/icons-material/Image"
import { Box, CircularProgress, IconButton, Paper, Tooltip, Typography } from "@mui/material"
import { File } from "graphql/types"
import { useState } from "react"
import { getImageURL } from "utils/getFileURL"

type ImageThumbnailProps = {
  image: File
  onDelete: (id: string) => void
  onEdit?: (image: File) => void
  onOpen?: (image: File) => void
  size?: number
  srcOverride?: string
}

export const ImageThumbnail = ({
  image,
  onDelete,
  onEdit,
  onOpen,
  size = 120,
  srcOverride,
}: ImageThumbnailProps) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const handleImageLoad = () => {
    setLoading(false)
    setError(false)
  }

  const handleImageError = () => {
    setLoading(false)
    setError(true)
  }

  return (
    <Paper
      sx={{
        p: 1,
        position: "relative",
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "transparent",
        overflow: "hidden",
        cursor: onOpen ? "pointer" : "default",
      }}
      onClick={onOpen ? () => onOpen(image) : undefined}
    >
      {/* Loading spinner */}
      {loading && !error && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
            zIndex: 1,
          }}
        >
          <CircularProgress size={30} />
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <ImageIcon sx={{ fontSize: 40, color: "#9e9e9e", mb: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Fehler beim Laden
          </Typography>
        </Box>
      )}

      {/* Actual image */}
      {!error && (
        <Box
          component="img"
          src={srcOverride || getImageURL(image.id)}
          alt={image.name}
          loading="lazy"
          onLoad={handleImageLoad}
          onError={handleImageError}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: 1,
            display: error ? "none" : "block",
            opacity: loading ? 0 : 1,
            transition: "opacity 0.3s ease-in-out",
          }}
        />
      )}

      {/* Image info overlay */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          padding: "4px 6px",
          borderTop: "1px solid rgba(0, 0, 0, 0.1)",
        }}
      >
        <Tooltip title={image.description || image.name}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "11px",
              fontWeight: "bold",
              color: "black",
            }}
          >
            {image.name}
          </Typography>
        </Tooltip>
        {image.description && (
          <Tooltip title={image.description}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "10px",
                color: "black",
              }}
            >
              {image.description}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* Action buttons */}
      <Box
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          display: "flex",
          gap: 0.5,
        }}
      >
        {onEdit && (
          <Tooltip title="Bearbeiten">
            <IconButton
              size="small"
              sx={{
                backgroundColor: "rgba(82, 78, 78, 0.9)",
                "&:hover": {
                  backgroundColor: "rgba(77, 73, 73, 1)",
                },
              }}
              onClick={(e) => {
                e.stopPropagation()
                onEdit(image)
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Löschen">
          <IconButton
            size="small"
            sx={{
              backgroundColor: "rgba(82, 78, 78, 0.9)",
              "&:hover": {
                backgroundColor: "rgba(77, 73, 73, 1)",
              },
            }}
            onClick={(e) => {
              e.stopPropagation()
              onDelete(image.id)
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  )
}
